/* Reglas de los archivos que se pueden subir desde el admin.
 *
 * Debe quedar alineado con Backend/src/config/limitesArchivos.js. El backend
 * es el que decide de verdad; esto solo existe para avisar al elegir el
 * archivo, y no despues de llenar el formulario entero.
 */

const MB = 1024 * 1024;

/* Firma de cada formato: los primeros bytes que lo identifican.
 *
 * Ni la extension ni el `type` que reporta el navegador prueban nada: los dos
 * salen del nombre del archivo, asi que renombrar cualquier cosa a .png
 * alcanza para pasarlos. El contenido es lo unico que no se puede falsear. */
const FIRMAS = [
  {
    tipo: "png",
    test: (b) =>
      [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every(
        (byte, i) => b[i] === byte
      ),
  },
  { tipo: "jpg", test: (b) => b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff },
  {
    // "RIFF" ....(4 bytes de tamano).... "WEBP"
    tipo: "webp",
    test: (b) => texto(b, 0, 4) === "RIFF" && texto(b, 8, 12) === "WEBP",
  },
  { tipo: "pdf", test: (b) => texto(b, 0, 5) === "%PDF-" },
];

// Lo que hay que leer del archivo para reconocer cualquiera de las firmas.
const BYTES_DE_FIRMA = 12;

const texto = (bytes, desde, hasta) =>
  String.fromCharCode(...bytes.slice(desde, hasta));

/* Las claves son los nombres de campo que espera el backend. La etiqueta es
   como se nombra el archivo en el mensaje, que no siempre coincide con el
   campo ("imagen_marca" no se le muestra a nadie). */
export const REGLAS = {
  imagen: {
    etiqueta: "Image",
    extensiones: [".png", ".jpg", ".jpeg", ".webp"],
    tipos: ["png", "jpg", "webp"],
    maxBytes: 15 * MB,
  },
  imagen_marca: {
    etiqueta: "Image",
    extensiones: [".png", ".jpg", ".jpeg", ".webp"],
    tipos: ["png", "jpg", "webp"],
    maxBytes: 15 * MB,
  },
  documento: {
    // "PDF must be a PDF file" no le dice nada a nadie: la etiqueta nombra al
    // campo, el formato lo pone el mensaje.
    etiqueta: "Document",
    extensiones: [".pdf"],
    tipos: ["pdf"],
    maxBytes: 15 * MB,
  },
};

/* Para el atributo `accept` del input. Vale la pena que sea la lista exacta y
   no "image/*": ese comodin ofrece GIF, BMP, SVG y HEIC, que el backend
   rechaza, y elegir un archivo para que despues te lo boten es peor que no
   poder elegirlo. */
export const acceptDe = (campo) => REGLAS[campo].extensiones.join(",");

const enMB = (bytes) => `${(bytes / MB).toFixed(1)} MB`;

/**
 * Lo que acepta un campo, en una linea: "PNG, JPG or WEBP  ·  up to 15 MB".
 *
 * Sale de las mismas reglas que validan, asi el cartel de la caja de subida
 * no puede terminar prometiendo algo distinto de lo que el sistema acepta.
 *
 * .jpg y .jpeg se muestran como una sola: al que sube le da lo mismo, y
 * listarlas por separado hace creer que son formatos distintos.
 */
export const descripcionDe = (campo) => {
  const regla = REGLAS[campo];
  if (!regla) return "";

  const vistos = [];
  regla.extensiones.forEach((ext) => {
    const nombre = ext.replace(".", "").toUpperCase();
    const unificado = nombre === "JPEG" ? "JPG" : nombre;
    if (!vistos.includes(unificado)) vistos.push(unificado);
  });

  const formatos =
    vistos.length === 1
      ? vistos[0]
      : `${vistos.slice(0, -1).join(", ")} or ${vistos[vistos.length - 1]}`;

  return `${formatos}  ·  up to ${Math.round(regla.maxBytes / MB)} MB`;
};

const extensionDe = (nombreArchivo) => {
  const limpio = String(nombreArchivo || "").toLowerCase();
  return limpio.includes(".") ? limpio.slice(limpio.lastIndexOf(".")) : "";
};

const listarExtensiones = (extensiones) => {
  const nombres = extensiones.map((ext) => ext.replace(".", "").toUpperCase());
  if (nombres.length === 1) return nombres[0];
  if (nombres.length === 2) return `${nombres[0]} or ${nombres[1]}`;
  return `${nombres.slice(0, -1).join(", ")}, or ${nombres[nombres.length - 1]}`;
};

/* Lee solo la cabecera, no el archivo entero: `slice` no copia nada, asi que
   esto cuesta lo mismo con una foto de 20 KB que con una de 14 MB. */
const detectarTipo = async (file) => {
  const cabecera = new Uint8Array(
    await file.slice(0, BYTES_DE_FIRMA).arrayBuffer()
  );
  return FIRMAS.find((firma) => firma.test(cabecera))?.tipo ?? null;
};

/**
 * Revisa un archivo recien elegido contra las reglas de su campo.
 *
 * Devuelve el motivo del rechazo listo para mostrar, o "" si esta bien. Un
 * string vacio es falsy, asi que el llamador puede hacer `if (motivo)` sin
 * comparar contra null.
 */
export const validarArchivo = async (file, campo) => {
  if (!file) return "";

  const regla = REGLAS[campo];
  if (!regla) throw new Error(`Campo de archivo sin reglas definidas: ${campo}`);

  const formatos = listarExtensiones(regla.extensiones);

  if (!regla.extensiones.includes(extensionDe(file.name))) {
    return `${regla.etiqueta} must be a ${formatos} file.`;
  }

  if (file.size > regla.maxBytes) {
    // Decir cuanto pesa evita el "y de cuanto es?" y el reintento a ciegas.
    return `${regla.etiqueta} is too large: ${enMB(file.size)}. The maximum is ${enMB(
      regla.maxBytes
    )}.`;
  }

  try {
    const tipo = await detectarTipo(file);
    if (!tipo || !regla.tipos.includes(tipo)) {
      return `That file is not a real ${formatos} file, even though its name says so.`;
    }
  } catch {
    /* Si no se pudo leer el archivo, no se bloquea: el backend lo va a revisar
       igual, y frenar aca por una falla del navegador seria dejar a alguien
       sin poder subir algo que en realidad es valido. */
    return "";
  }

  return "";
};
